// src/utils/ViewReport/viewReportSession.js

/**
 * Session utilities for view-report data capture.
 * Mirrors the pattern from src/utils/Onboarding/session.js
 */

/**
 * Returns an ISO 8601 string using UTC timezone with millisecond precision.
 */
export function getUtcIsoTimestamp(dateLike = null) {
  const date = dateLike instanceof Date ? dateLike : dateLike ? new Date(dateLike) : new Date();
  return date.toISOString();
}

function padNumber(value, length = 2) {
  return String(value).padStart(length, '0');
}

function buildSortableTimestamp(dateLike = null) {
  const date = dateLike instanceof Date ? dateLike : dateLike ? new Date(dateLike) : new Date();
  return [
    padNumber(date.getUTCFullYear(), 4),
    padNumber(date.getUTCMonth() + 1, 2),
    padNumber(date.getUTCDate(), 2),
    'T',
    padNumber(date.getUTCHours(), 2),
    padNumber(date.getUTCMinutes(), 2),
    padNumber(date.getUTCSeconds(), 2),
    padNumber(date.getUTCMilliseconds(), 3),
    'Z',
  ].join('');
}

/**
 * Generates a view-report session identifier that sorts lexicographically by creation moment.
 * Uses 'vr_' prefix to distinguish from onboarding session IDs.
 * Example output: vr_20250203T141533187Z-d0f6b21c
 */
export function generateViewReportSessionId(dateLike = null) {
  const sortable = buildSortableTimestamp(dateLike);
  const randomSuffix = Math.random().toString(36).slice(2, 10);
  return `vr_${sortable}-${randomSuffix}`;
}

/**
 * Generates a view-report session identifier that includes the requestId for easier lookup.
 * Format: vr_{requestId}_{timestamp}-{random}
 * Example: vr_req_1773103717530_0n1h5r7_20250203T141533187Z-d0f6b21c
 */
export function generateViewReportSessionIdWithRequestId(requestId, dateLike = null) {
  const sortable = buildSortableTimestamp(dateLike);
  const randomSuffix = Math.random().toString(36).slice(2, 10);
  const sanitizedRequestId = (requestId || 'unknown').replace(/[^a-zA-Z0-9_-]/g, '_');
  return `vr_${sanitizedRequestId}_${sortable}-${randomSuffix}`;
}

export function describeViewReportSession(sessionId, createdAt = null) {
  return {
    sessionId,
    createdAt: createdAt ?? getUtcIsoTimestamp(),
  };
}

/**
 * Extract UTM parameters from URL search string
 */
export function extractUtmParameters(searchString) {
  const params = new URLSearchParams(searchString);
  const utmParams = {};
  
  ['source', 'medium', 'campaign', 'term', 'content'].forEach((param) => {
    const value = params.get(`utm_${param}`);
    if (value) utmParams[`utm_${param}`] = value;
  });
  
  // Extract click IDs
  const gclid = params.get('gclid');
  if (gclid) utmParams.gclid = gclid;
  
  const fbclid = params.get('fbclid');
  if (fbclid) utmParams.fbclid = fbclid;
  
  return Object.keys(utmParams).length > 0 ? utmParams : null;
}
